// Device list localStorage helpers: persist search, column visibility, and view preferences.

import type { ExtraColumnKey, DeviceFiltersValue } from '../../../../types/DeviceListTypes';
import { extraColumns, DEFAULT_DEVICE_FILTERS } from '../../../../types/DeviceListTypes';
import { STORAGE_SEARCH_KEY, STORAGE_MASTER_COLS_KEY, STORAGE_EXTRA_COLS_KEY } from './deviceListConstants';

const STORAGE_ADJACENT_PAIRS_KEY = 'fleetmanager:deviceList:showAdjacentPairs';
const STORAGE_FILTERS_SIDEBAR_KEY = 'fleetmanager:deviceList:filtersSidebarOpen';
const STORAGE_FILTERS_KEY = 'fleetmanager:deviceList:filters';

/** Restore the device filter selections, merged over current defaults so newly
 *  added filter keys keep working when reading older persisted state. */
export function loadFilters(): DeviceFiltersValue {
  if (typeof window === 'undefined') return { ...DEFAULT_DEVICE_FILTERS };
  try {
    const raw = localStorage.getItem(STORAGE_FILTERS_KEY);
    if (!raw) return { ...DEFAULT_DEVICE_FILTERS };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_DEVICE_FILTERS };
    const merged = { ...DEFAULT_DEVICE_FILTERS, ...(parsed as Record<string, unknown>) } as DeviceFiltersValue;
    merged.lastCmdStatuses = Array.isArray((parsed as DeviceFiltersValue).lastCmdStatuses)
      ? (parsed as DeviceFiltersValue).lastCmdStatuses
      : [];
    return merged;
  } catch {
    return { ...DEFAULT_DEVICE_FILTERS };
  }
}

/** Persist the device filter selections across navigation and sessions. */
export function saveFilters(filters: DeviceFiltersValue): void {
  try {
    localStorage.setItem(STORAGE_FILTERS_KEY, JSON.stringify(filters));
  } catch {}
}

export function loadShowAdjacentPairs(): boolean {
  if (typeof window === 'undefined') return false;
  try { return localStorage.getItem(STORAGE_ADJACENT_PAIRS_KEY) === '1'; } catch { return false; }
}

export function saveShowAdjacentPairs(value: boolean): void {
  try { localStorage.setItem(STORAGE_ADJACENT_PAIRS_KEY, value ? '1' : '0'); } catch {}
}

export function loadFiltersSidebarOpen(): boolean {
  if (typeof window === 'undefined') return false;
  try { return localStorage.getItem(STORAGE_FILTERS_SIDEBAR_KEY) === '1'; } catch { return false; }
}

export function saveFiltersSidebarOpen(value: boolean): void {
  try { localStorage.setItem(STORAGE_FILTERS_SIDEBAR_KEY, value ? '1' : '0'); } catch {}
}

/** Restore search query from localStorage (persisted across reloads). */
export function loadSearchQuery(): string {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem(STORAGE_SEARCH_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Restore which master columns are visible from localStorage. */
export function loadVisibleMasterColumns(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_MASTER_COLS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

const DEFAULT_EXTRA_COLUMNS: ExtraColumnKey[] = [
  '__online',
  '__mode',
  '__fw',
  '__batt',
  '__last',
  '__tls',
  '__attr_race_count',
  '__attr_waterproofed_by',
  '__race_log',
  '__debug_log',
  '__batt_ma',
  '__reason',
  '__gga',
  '__busy',
  '__busy_from',
  '__busy_reason',
  '__last_cmd_id',
  '__last_cmd_type',
  '__last_cmd_target',
  '__last_cmd_status',
  '__last_cmd_ack_percent',
  '__last_cmd_updated',
];

/** Restore which extra columns are visible; sanitize against allowed set. */
export function loadVisibleExtraColumns(): ExtraColumnKey[] {
  const allowed = new Set<ExtraColumnKey>(extraColumns.map((c) => c.key));

  const sanitize = (arr: unknown[]): ExtraColumnKey[] =>
    arr
      .filter((x): x is ExtraColumnKey => typeof x === 'string')
      .filter((k) => allowed.has(k));

  if (typeof window === 'undefined') return sanitize(DEFAULT_EXTRA_COLUMNS);

  try {
    const raw = localStorage.getItem(STORAGE_EXTRA_COLS_KEY);
    if (!raw) return sanitize(DEFAULT_EXTRA_COLUMNS);

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return sanitize(DEFAULT_EXTRA_COLUMNS);

    const migrated = parsed.flatMap((x) =>
      x === '__lifecycle' ? [] : [x],
    );
    const out = sanitize(migrated);
    return out.length ? out : sanitize(DEFAULT_EXTRA_COLUMNS);
  } catch {
    return sanitize(DEFAULT_EXTRA_COLUMNS);
  }
}
